import {DragAndDrop} from '@github-ui/drag-and-drop'
import {IssueRow} from '@github-ui/list-view-items-issues-prs/IssueRow'
import {PullRequestRow} from '@github-ui/list-view-items-issues-prs/PullRequestRow'
import type {QUERY_FIELDS} from '@github-ui/list-view-items-issues-prs/Queries'
import {useIssueIndexSecondaryQuery} from '@github-ui/list-view-items-issues-prs/use-issue-index-secondary-query'
import {noop} from '@github-ui/noop'
import type {NavigateOptions, To} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo} from 'react'

import styles from './RepositoryMilestone.module.css'
import type {ItemNodeType} from './types'

type MilestoneIssuesListPageProps = {
  orderedNodes: ItemNodeType[]
  checkedItems: Map<string, ItemNodeType>
  itemSelected: (id: string, node: ItemNodeType, selected: boolean) => void
  getMetadataHref: (queryField: keyof typeof QUERY_FIELDS, metadataName: string) => string
  getAdditiveMetadataHref: (queryField: keyof typeof QUERY_FIELDS, metadataName: string) => string
  withDragAndDrop: boolean
  scopedRepository: {id: string; name: string; owner: string; is_archived: boolean}
  handleNavigate: (issueNumber: number, to: To, navOptions?: NavigateOptions) => Promise<void>
}

export function MilestoneIssuesListPage({
  orderedNodes,
  checkedItems,
  itemSelected,
  getMetadataHref,
  getAdditiveMetadataHref,
  withDragAndDrop,
  scopedRepository,
  handleNavigate,
}: MilestoneIssuesListPageProps) {
  const navigate = useNavigate()
  const nodes = useMemo(() => orderedNodes.map(node => node.id), [orderedNodes])
  const milestoneIssuesLazyData = useIssueIndexSecondaryQuery({nodes, includeReactions: false})

  const items = orderedNodes?.map((node, index) => {
    const sharedRowData = {
      isActive: false,
      isSelected: node && checkedItems.has(node.id),
      onSelect: (selected: boolean) => node && itemSelected(node.id, node, selected),
      onSelectRow: noop,
      getMetadataHref,
      getAdditiveMetadataHref,
      reactionEmojiToDisplay: {reaction: '', reactionEmoji: ''},
      sortingItemSelected: '',
      scopedRepository,
      listItemMetadata: milestoneIssuesLazyData,
    }
    if (!withDragAndDrop) {
      if (node.__typename === 'Issue' && node.issueRow) {
        return (
          <IssueRow
            key={node.id}
            issueKey={node.issueRow}
            onNavigate={(to: To, navOptions?: NavigateOptions) => handleNavigate(node.number, to, navOptions)}
            {...sharedRowData}
          />
        )
      } else if (node.__typename === 'PullRequest' && node.pullRequestRow) {
        return (
          <PullRequestRow key={node.id} pullRequestKey={node.pullRequestRow} onNavigate={navigate} {...sharedRowData} />
        )
      }
      return null
    }

    return (
      <DragAndDrop.Item
        hideTrigger
        as="li"
        index={index}
        id={node.id}
        key={node.id}
        title={''}
        style={{display: 'flex', width: '100%'}}
        className={styles.dragAndDropItem}
        tabIndex={-1} // tabIndex is handled by useRovingTabIndex for tab navigation
        elementId={`list-view-node-${node.id}`}
      >
        {node.__typename === 'Issue' && node.issueRow ? (
          <IssueRow
            key={node.id}
            issueKey={node.issueRow}
            onNavigate={(to: To, navOptions?: NavigateOptions) => handleNavigate(node.number, to, navOptions)}
            {...sharedRowData}
            as="div"
            role="none"
          />
        ) : node.__typename === 'PullRequest' && node.pullRequestRow ? (
          <PullRequestRow
            key={node.id}
            pullRequestKey={node.pullRequestRow}
            onNavigate={navigate}
            {...sharedRowData}
            as="div"
            role="none"
          />
        ) : null}
      </DragAndDrop.Item>
    )
  })
  return <>{items}</>
}
